import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { getToken, removeToken } from '@/utils/storage';
import { profileApi } from '@/api/profile';
import { isTokenValidated, setTokenValidated } from '@/utils/authState';

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false, title: '登录' }
  },
  {
    path: '/',
    component: () => import('@/components/layout/AppLayout.vue'),
    redirect: '/welcome',
    meta: { requiresAuth: true },
    children: [
      // 欢迎首页
      {
        path: 'welcome',
        name: 'Welcome',
        component: () => import('@/views/WelcomeView.vue'),
        meta: { title: '欢迎首页', icon: 'HomeFilled' }
      },
      // 商家管理
      {
        path: 'merchant',
        name: 'MerchantList',
        component: () => import('@/views/merchant/MerchantList.vue'),
        meta: { title: '商家管理', icon: 'Shop' }
      },
      {
        path: 'merchant/:id',
        name: 'MerchantDetail',
        component: () => import('@/views/merchant/MerchantDetail.vue'),
        meta: { title: '商家详情', hidden: true }
      },
      {
        path: 'merchant-audit',
        name: 'MerchantAudit',
        component: () => import('@/views/merchant/MerchantAudit.vue'),
        meta: { title: '入驻审核', icon: 'Checked' }
      },
      // 案例管理
      {
        path: 'work',
        name: 'WorkManagement',
        component: () => import('@/views/work/WorkManagement.vue'),
        meta: { title: '案例管理', icon: 'Picture' }
      },
      // 内容审核（作品人工复核）
      {
        path: 'work-review',
        name: 'WorkReview',
        component: () => import('@/views/work/WorkReview.vue'),
        meta: { title: '内容审核', icon: 'View' }
      },
      // 媒体管理
      {
        path: 'media',
        name: 'MediaManagement',
        component: () => import('@/views/media/MediaManagement.vue'),
        meta: { title: '媒体管理', icon: 'FolderOpened' }
      },
      // 财务管理
      {
        path: 'finance',
        name: 'Finance',
        component: () => import('@/views/finance/AccountDetail.vue'),
        meta: { title: '财务管理', icon: 'Wallet' }
      },
      // 访客管理
      {
        path: 'visitor',
        name: 'VisitorManagement',
        component: () => import('@/views/visitor/VisitorManagement.vue'),
        meta: { title: '访客管理', icon: 'User' }
      },
      // 登录日志（挂在「访客管理」下，与访客数据分离的另一类记录）
      {
        path: 'visitor/login-logs',
        name: 'LoginLogManage',
        component: () => import('@/views/visitor/LoginLogManage.vue'),
        meta: { title: '登录日志', icon: 'Document', hidden: true }
      },
      // 内容设置
      {
        path: 'content',
        name: 'ContentSettings',
        component: () => import('@/views/content/ContentSettings.vue'),
        meta: { title: '内容设置', icon: 'Document' }
      },
      // 使用说明管理
      {
        path: 'help',
        name: 'HelpManagement',
        component: () => import('@/views/content/HelpManagement.vue'),
        meta: { title: '使用说明', icon: 'Reading' }
      },
      // 公共案例库管理
      {
        path: 'public-case',
        name: 'PublicCaseManagement',
        component: () => import('@/views/content/PublicCaseList.vue'),
        meta: { title: '公共案例库管理', icon: 'Collection' }
      },
      // 公共案例库协议
      {
        path: 'public-case-agreement',
        name: 'PublicCaseAgreement',
        component: () => import('@/views/content/PublicCaseAgreement.vue'),
        meta: { title: '公共案例库协议', icon: 'DocumentChecked' }
      },
      // 套餐管理
      {
        path: 'packages',
        name: 'PackageManagement',
        component: () => import('@/views/package/PackageManagement.vue'),
        meta: { title: '套餐管理', icon: 'Box' }
      },
      // AI修图公共预设管理
      {
        path: 'ai-presets',
        name: 'AiPresetManage',
        component: () => import('@/views/package/AiPresetManage.vue'),
        meta: { title: 'AI公共预设', icon: 'MagicStick' }
      },
      // AI修图点数批次管理
      {
        path: 'ai-batch',
        name: 'AiBatchManage',
        component: () => import('@/views/ai/AiBatchManage.vue'),
        meta: { title: 'AI点数批次', icon: 'Coin' }
      },
      // 个人中心
      {
        path: 'profile',
        name: 'ProfileCenter',
        component: () => import('@/views/profile/ProfileCenter.vue'),
        meta: { title: '个人中心', hidden: true }
      },
      // 管理员管理
      {
        path: 'admin',
        name: 'AdminManagement',
        component: () => import('@/views/profile/AdminManagement.vue'),
        meta: { title: '管理员管理', icon: 'UserFilled' }
      },
      // 客服管理
      {
        path: 'customer-service',
        name: 'CustomerService',
        component: () => import('@/views/customerService/CustomerServiceManagement.vue'),
        meta: { title: '客服管理', icon: 'Service' }
      },
      // 需求提报（商户提交的功能需求，超管审核后公开）
      {
        path: 'feature-request',
        name: 'FeatureRequest',
        component: () => import('@/views/merchant/FeatureRequestManage.vue'),
        meta: { title: '需求提报', icon: 'ChatDotRound' }
      },
      // 违规举报信息（C端用户提交的举报列表与详情）
      {
        path: 'violation-report',
        name: 'ViolationReportManage',
        component: () => import('@/views/content/ViolationReportManage.vue'),
        meta: { title: '违规举报信息', icon: 'Warning' }
      },
      // 短信管理
      {
        path: 'sms-template',
        name: 'SmsTemplate',
        component: () => import('@/views/sms/SmsTemplateManage.vue'),
        meta: { title: '短信模板', icon: 'Message' }
      },
      {
        path: 'sms-sign',
        name: 'SmsSign',
        component: () => import('@/views/sms/SmsSignManage.vue'),
        meta: { title: '短信签名', icon: 'Message' }
      },
      {
        path: 'sms-log',
        name: 'SmsLog',
        component: () => import('@/views/sms/SmsLogManage.vue'),
        meta: { title: '发送记录', icon: 'Message' }
      },
      {
        path: 'sms-notice-policy',
        name: 'SmsNoticePolicy',
        component: () => import('@/views/sms/NoticePolicy.vue'),
        meta: { title: '到期提醒策略', icon: 'Message' }
      },
      {
        path: 'sms-batch',
        name: 'SmsBatch',
        component: () => import('@/views/sms/SmsBatchManage.vue'),
        meta: { title: '短信包明细', icon: 'Box' }
      },
      // 功能管理
      {
        path: 'feature',
        name: 'FeatureManagement',
        component: () => import('@/views/feature/FeatureManagement.vue'),
        meta: { title: '功能管理', icon: 'Setting' }
      },
      // 行业分类管理
      {
        path: 'system/industry-management',
        name: 'IndustryManagement',
        component: () => import('@/views/system/IndustryManagement.vue'),
        meta: { title: '行业分类管理', icon: 'Grid' }
      },
      // 前端图片展示规格
      {
        path: 'system/image-spec',
        name: 'ImageSpecConfig',
        component: () => import('@/views/system/ImageSpecConfig.vue'),
        meta: { title: '图片展示规格', icon: 'Picture' }
      },
      // 自动续费扫描（手动触发校验）
      {
        path: 'system/auto-renew',
        name: 'AutoRenewManage',
        component: () => import('@/views/system/AutoRenewManage.vue'),
        meta: { title: '自动续费扫描', icon: 'Refresh' }
      },
      // 模板图片管理
      {
        path: 'template-images',
        name: 'TemplateImageManage',
        component: () => import('@/views/template/TemplateImageManage.vue'),
        meta: { title: '模板图片管理', icon: 'Picture' }
      },
      // 照片直播模板管理
      {
        path: 'live-template',
        name: 'LiveTemplateManage',
        component: () => import('@/views/template/LiveTemplateManage.vue'),
        meta: { title: '直播模板管理', icon: 'Film' }
      },
      // 照片直播模板分类管理
      {
        path: 'live-template-category',
        name: 'LiveTemplateCategoryManage',
        component: () => import('@/views/template/LiveTemplateCategoryManage.vue'),
        meta: { title: '直播模板分类', icon: 'Collection' }
      },
      // 模板七自定义布局预设管理
      {
        path: 'template7-preset',
        name: 'Template7PresetManage',
        component: () => import('@/views/template/Template7PresetManage.vue'),
        meta: { title: '模板七自定义预设', icon: 'SetUp' }
      },
      // 客户端管理
      {
        path: 'clients',
        name: 'ClientManagement',
        component: () => import('@/views/device/ClientManagement.vue'),
        meta: { title: '客户端管理', icon: 'Monitor' }
      },
      // 商家小程序管理
      {
        path: 'merchant-mini-program',
        name: 'MiniProgramList',
        component: () => import('@/views/miniapp/MiniProgramList.vue'),
        meta: { title: '小程序管理', icon: 'Cellphone' }
      }
    ]
  },
  // 404
  {
    path: '/:pathMatch(.*)*',
    redirect: '/welcome'
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// 路由守卫
router.beforeEach(async (to, _from, next) => {
  // 设置页面标题
  const title = to.meta?.title as string | undefined;
  document.title = `${title || '禧匣·云案例小程序管理系统'} - 管理后台`;
  
  const token = getToken();
  const requiresAuth = to.meta?.requiresAuth !== false;
  
  // 1. 完全没有 token
  if (!token) {
    if (requiresAuth) {
      next({ path: '/login', query: { redirect: to.fullPath } });
    } else {
      next();
    }
    return;
  }
  
  // 2. 有 token 但还没向后端校验过有效性 → 校验一次
  if (!isTokenValidated()) {
    try {
      await profileApi.validateToken();
      setTokenValidated(true);
    } catch {
      // token 失效（过期/被踢/服务端重启等）：清除并跳登录
      removeToken();
      setTokenValidated(false);
      if (requiresAuth) {
        next({ path: '/login', query: { redirect: to.fullPath } });
      } else {
        next(); // 本身就是去登录页
      }
      return;
    }
  }
  
  // 3. token 有效
  if (!requiresAuth && to.path === '/login') {
    // 已登录访问登录页，跳转首页
    next('/welcome');
  } else {
    next();
  }
});

export default router;
